import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	Links,
	Meta,
	NavLink,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useRouteLoaderData,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from '@tfcore/ui/dropdown-menu'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { setSentryUser } from '#app/features/users/utils/authentication/setSentryUser.server.ts'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { TotalFusionTitle } from './components/totalfusion-logo.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { getCachedFeatureFlags } from './features/shared/utils/feature-flags/getCachedFeatureFlags.server.ts'
import { useFeatureFlag } from './features/shared/utils/feature-flags/useFeatureFlag.ts'
import { getUserProfile } from './features/users/database/getUserProfile.server.ts'
import { authenticateUser } from './features/users/utils/authentication/authenticateUser.server'
import { getCachedMemberIdentity } from './features/users/utils/getCachedMemberIdentity.server.ts'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import sonnerStyleSheetUrl from './styles/sonner.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { isMobile, useIsMobile, useOptionalUser } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload CSS as a resource to avoid render blocking
		{ rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: sonnerStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'TotalFusion' : 'Error | TotalFusion' },
		{ name: 'description', content: `TotalFusion` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')

	const { user, headers: authHeaders } = await time(
		() => authenticateUser(request),
		{ timings, type: 'authenticate user' },
	)

	setSentryUser(user)

	const profile = await time(() => (user ? getUserProfile(user.id) : null), {
		timings,
		type: 'find profile',
	})

	const memberIdentity = await time(
		() =>
			profile?.memberProfileId
				? getCachedMemberIdentity(profile.memberProfileId)
				: null,
		{
			timings,
			type: 'get cached member identity',
		},
	)

	const featureFlags = await time(
		() =>
			profile?.memberProfileId
				? getCachedFeatureFlags(profile.memberProfileId)
				: null,
		{
			timings,
			type: 'get cached feature flags',
		},
	)

	const { toast, headers: toastHeaders } = await getToast(request)

	const honeyProps = honeypot.getInputProps()

	const userMobile = isMobile(request)

	return json(
		{
			user,
			profile,
			memberIdentity,
			featureFlags,
			userMobile,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				authHeaders,
			),
		},
	)
}

export function useRootLoaderData() {
	const data = useRouteLoaderData<typeof loader>('root')
	if (!data) throw new Error('Root loader data is required')
	return data satisfies NonNullable<typeof data>
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html lang="en" className={`${theme} h-full`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,maximum-scale=1, user-scalable=no, viewport-fit=cover"
				/>
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const theme = useTheme()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)
	const isMobile = useIsMobile()

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="flex h-screen flex-col justify-between">
				{!isMobile && data.user && <Header />}
				<div className="flex-1">
					<Outlet />
				</div>
				{!isMobile && data.user && <Footer />}
			</div>
			<EpicToaster
				closeButton
				position="top-center"
				className="z-50"
				theme={theme}
			/>
			<EpicProgress />
		</Document>
	)
}

function Header() {
	return (
		<header className="sticky top-0 z-50 bg-background">
			<nav className="flex w-full items-center justify-between gap-2 bg-[#0f0f0f] px-6 pb-2 pt-2">
				<MenuDropdown />
				<TotalFusionTitle width={210} height={24} className="w-full" />
				<NotificationBell />
			</nav>
		</header>
	)
}

function NotificationBell() {
	const { isEnabled: isNotificationsEnabled } = useFeatureFlag(
		'notifications',
		false,
	)

	if (!isNotificationsEnabled) return null

	return (
		<button className="h-14 w-14">
			<Icon name="bell" size="lg" />
		</button>
	)
}

function Footer() {
	const user = useOptionalUser()
	const { isEnabled: isRecoveryEnabled } = useFeatureFlag('recovery', false)
	const { isEnabled: isSpaEnabled } = useFeatureFlag('spa', false)

	if (!user) return null

	return (
		<footer className="sticky bottom-0 mt-2 bg-background">
			<nav className="flex h-24 w-full items-center justify-between gap-2 bg-[#0f0f0f] px-6 pb-8 pt-4 sm:px-56">
				<NavLink
					to="/app/home"
					className={({ isActive, isPending }) =>
						isActive || isPending ? 'text-primary' : undefined
					}
				>
					<div className="flex flex-col items-center">
						<Icon name="home" className="h-7 w-7" />
						<span className="text-body-2xs">Home</span>
					</div>
				</NavLink>
				<NavLink
					to="/calendar/agenda"
					className={({ isActive, isPending }) =>
						isActive || isPending ? 'text-primary' : undefined
					}
				>
					<div className="flex flex-col items-center">
						<Icon name="calendar" className="h-7 w-7" />
						<span className="text-body-2xs">Calendar</span>
					</div>
				</NavLink>
				<NavLink
					to="/classes"
					className={({ isActive, isPending }) =>
						isActive || isPending ? 'text-primary' : undefined
					}
				>
					<div className="flex flex-col items-center">
						<Icon name="class" className="h-7 w-7" />
						<span className="text-body-2xs">Classes</span>
					</div>
				</NavLink>
				{isSpaEnabled && (
					<NavLink
						to="/spa"
						className={({ isActive, isPending }) =>
							isActive || isPending ? 'text-primary' : undefined
						}
					>
						<div className="flex flex-col items-center">
							<Icon name="spa" className="h-7 w-7" />
							<span className="text-body-2xs">Spa</span>
						</div>
					</NavLink>
				)}
				{isRecoveryEnabled && (
					<NavLink
						to={`/recovery/`}
						className={({ isActive, isPending }) =>
							isActive || isPending ? 'text-primary' : undefined
						}
					>
						<div className="flex flex-col items-center">
							<Icon name="home" className="h-7 w-7" />
							<span className="text-body-2xs">Recovery</span>
						</div>
					</NavLink>
				)}
			</nav>
		</footer>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function MenuDropdown() {
	const { isEnabled: isCrecheEnabled } = useFeatureFlag('creche', false)
	const { isEnabled: isContactEnabled } = useFeatureFlag('contact', false)
	const { isEnabled: isFeedbackEnabled } = useFeatureFlag('feedback', false)
	const { isEnabled: isFreezeEnabled } = useFeatureFlag('freeze', false)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button className="h-14 w-14">
					<Icon name="hamburger-menu" size="lg" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent
					align="start"
					className="rounded-xl border-none bg-neutral-700 hover:bg-neutral-700"
				>
					<DropdownMenuItem asChild>
						<Link to="/profile">Profile</Link>
					</DropdownMenuItem>
					{isCrecheEnabled && (
						<DropdownMenuItem asChild>
							<Link to="/creche">Creche</Link>
						</DropdownMenuItem>
					)}
					{isContactEnabled && (
						<DropdownMenuItem asChild>
							<Link to="/contact">Contact</Link>
						</DropdownMenuItem>
					)}
					{isFreezeEnabled && (
						<DropdownMenuItem asChild>
							<Link to="/freeze-membership">Freeze Membership</Link>
						</DropdownMenuItem>
					)}
					{isFeedbackEnabled && (
						<DropdownMenuItem asChild>
							<Link to="/app/member-feedback">Member Feedback</Link>
						</DropdownMenuItem>
					)}
					<DropdownMenuItem asChild>
						<Link to="/profile/change-password">Change Password</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to="/logout">Logout</Link>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
